import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { getMaxAttachmentByteCountForEmailConfigurations } from '../../../../extViewmodels/Utils';
import { EventLogger } from '../../../../models/Logging';
import { getFileSizeStringValue } from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	AttachmentsToBeUploadedViewModel,
	IFileAttachment,
	IImpersonationContext,
	IInProgressFileAttachment,
} from '../../../../viewmodels/AppViewModels';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import { THEME } from '../../../components-v2/Theme/theme';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../LoadingSpinner';
import { PaperClipIcon } from '../../svgs/icons/PaperClipIcon';
import { PrivateVisibilityIcon } from '../../svgs/icons/PrivateVisibilityIcon';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	emailAttachments?: AttachmentsToBeUploadedViewModel<File>;
	hasExceededMaxByteCount?: boolean;
	impersonationContext?: IImpersonationContext;
	inProgressAttachments?: IInProgressFileAttachment[];
	onRequestAttachFiles?: () => void;
	onRequestRemoveSavedEmailAttachment?(savedAttachment: IFileAttachment): Promise<void>;
	onToggleSendAttachmentsAsLink?(value: boolean): void;
	readonly?: boolean;
	savedAttachments?: IFileAttachment[];
	sendAttachmentsAsLink?: boolean;
	sendAsLinkRequired?: boolean;
}

function FileChip({
	size,
	name,
	onRemoveClicked,
	pending,
	exceedsMaxByteCount,
	isSecureLink,
}: {
	size: number;
	name: string;
	onRemoveClicked?(e: React.MouseEvent<HTMLElement>): void;
	pending?: boolean;
	exceedsMaxByteCount?: boolean;
	isSecureLink?: boolean;
}) {
	const fileSizeStringValue = getFileSizeStringValue(size);
	return (
		<div
			className={css(
				styleSheet.chip,
				exceedsMaxByteCount ? styleSheet.chipError : isSecureLink ? styleSheet.chipSecureLink : styleSheet.chipSuccess,
				pending ? styleSheet.chipPending : null
			)}
		>
			{isSecureLink && <PrivateVisibilityIcon fillColor={THEME.colors.primary.main} />}
			<span className={css(styleSheet.chipName)} title={name}>
				{name}
			</span>
			<span className={css(styleSheet.chipSize)} title={fileSizeStringValue}>
				{fileSizeStringValue}
			</span>
			{pending ? (
				<LoadingSpinner type='tiny' />
			) : onRemoveClicked ? (
				<DeprecatedCloseButton styles={[styleSheet.chipRemove]} onClick={onRemoveClicked} />
			) : null}
		</div>
	);
}

const TOGGLE_CHECKBOX_ID = 'email-attachments-send-as-link';
const TWENTY_MB = 20 * 1024 * 1024;

export function hasExceededEmailAttachmentLimit({
	emailAttachments,
	savedAttachments,
	inProgressAttachments,
	sendAttachmentsAsLink,
	maxBytes,
}: {
	emailAttachments?: AttachmentsToBeUploadedViewModel<File>;
	savedAttachments?: IFileAttachment[];
	inProgressAttachments?: IInProgressFileAttachment[];
	sendAttachmentsAsLink?: boolean;
	maxBytes: number;
}) {
	const totalBytes =
		(emailAttachments?.byteCount ?? 0) +
		(savedAttachments || []).reduce((count, x) => count + x.fileSize, 0) +
		(inProgressAttachments?.reduce((count, x) => count + x.fileSize, 0) ?? 0);
	return sendAttachmentsAsLink
		? totalBytes > TWENTY_MB
		: emailAttachments?.hasExceededMaxByteCount || totalBytes > maxBytes;
}

export const EmailAttachments = observer(function EmailAttachmentsBase({
	className,
	emailAttachments,
	hasExceededMaxByteCount: hasExceededMaxByteCountProp,
	impersonationContext,
	inProgressAttachments,
	onRequestAttachFiles,
	onRequestRemoveSavedEmailAttachment,
	onToggleSendAttachmentsAsLink,
	readonly,
	savedAttachments,
	sendAttachmentsAsLink,
	sendAsLinkRequired,
}: IProps) {
	const userSession = useUserSession();
	const canAddFiles = !readonly && !!emailAttachments && !!onRequestAttachFiles;
	const [isDragging, setIsDragging] = React.useState(false);

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		if (!canAddFiles) {
			return;
		}
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		if (!canAddFiles) {
			return;
		}
		if (!e.currentTarget.contains(e.relatedTarget as Node)) {
			setIsDragging(false);
		}
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		if (!canAddFiles) {
			return;
		}
		e.preventDefault();
		setIsDragging(false);
		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			emailAttachments.add(files);
			EventLogger.logInput('EmailAttachments', 'Drop', 'Click', { count: files.length });
		}
	};

	const onRemoveSavedEmailAttachment = (savedAttachment: IFileAttachment) => (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		e.preventDefault();
		onRequestRemoveSavedEmailAttachment?.(savedAttachment);
	};

	const onRemoveClicked = (file: File) => (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		e.preventDefault();
		emailAttachments.remove([file]);
		EventLogger.logInput('EmailAttachments', 'Remove', 'Click', { size: file.size, type: file.type });
	};

	const totalCount =
		(emailAttachments?.attachments?.length ?? 0) +
		(savedAttachments?.length ?? 0) +
		(inProgressAttachments?.length ?? 0);

	const maxBytes = getMaxAttachmentByteCountForEmailConfigurations(
		impersonationContext?.account?.emailProviderConfigurations || userSession.account.emailProviderConfiguration
	);

	if (totalCount === 0) {
		if (!canAddFiles) {
			return null;
		}
		return (
			<div
				aria-label='Attach files'
				className={`email-attachments ${className || ''} ${css(
					styleSheet.dropZone,
					isDragging ? styleSheet.dropZoneDragging : null
				)}`}
				onClick={onRequestAttachFiles}
				onDragEnter={handleDragOver}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onRequestAttachFiles();
					}
				}}
				role='button'
				tabIndex={0}
			>
				<div className={css(styleSheet.dropZoneCenter)}>
					<span className={css(styleSheet.dropZoneLabel)}>
						<PaperClipIcon width={16} height={16} stroke={THEME.colors.text.light} />
						Drag files here, or <span className={css(styleSheet.dropZoneBrowse)}>browse</span>
					</span>
					<span className={css(styleSheet.dropZoneSubtitle)}>
						{onToggleSendAttachmentsAsLink
							? 'Max file size is 5 MB. Larger files are sent automatically as a secure download link.'
							: `Max attachment size is ${getFileSizeStringValue(maxBytes)}.`}
					</span>
				</div>
			</div>
		);
	}

	const hasExceededMaxByteCount =
		hasExceededMaxByteCountProp ??
		hasExceededEmailAttachmentLimit({
			emailAttachments,
			savedAttachments,
			inProgressAttachments,
			sendAttachmentsAsLink,
			maxBytes,
		});

	return (
		<div
			className={`email-attachments ${className || ''} ${css(
				styleSheet.container,
				isDragging ? styleSheet.dropZoneDragging : null
			)}`}
			onDragEnter={handleDragOver}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{!!hasExceededMaxByteCount && (
				<div className={css(styleSheet.error)}>
					{sendAttachmentsAsLink
						? `You've exceeded the secure file link size limit of ${getFileSizeStringValue(TWENTY_MB)}.`
						: `You've exceeded the attachment size limit of ${getFileSizeStringValue(maxBytes)}.`}
				</div>
			)}
			<div className={css(styleSheet.headerRow)}>
				<span className={css(styleSheet.headerLabel)}>
					{totalCount} attachment{totalCount !== 1 ? 's' : ''}
				</span>
				<div className={css(styleSheet.headerActions)}>
					{canAddFiles && (
						<Button variant='outline' size='sm' onClick={onRequestAttachFiles}>
							<PaperClipIcon width={16} height={16} />
							Attach files
						</Button>
					)}
					{!!onToggleSendAttachmentsAsLink && (
						<div className={css(styleSheet.toggleRow)}>
							<Checkbox
								checked={sendAsLinkRequired || !!sendAttachmentsAsLink}
								disabled={sendAsLinkRequired}
								id={TOGGLE_CHECKBOX_ID}
								onCheckedChange={value => onToggleSendAttachmentsAsLink(value === true)}
							/>
							<label className={css(styleSheet.toggleLabel)} htmlFor={TOGGLE_CHECKBOX_ID}>
								{sendAsLinkRequired ? 'Send as download link (required for large files)' : 'Send as download link'}
							</label>
						</div>
					)}
				</div>
			</div>
			<div className={css(styleSheet.chipList)}>
				{inProgressAttachments?.map(x => (
					<FileChip
						key={x.uploadTaskId}
						name={x.fileName}
						onRemoveClicked={!readonly && x.id ? onRemoveSavedEmailAttachment(x as IFileAttachment) : undefined}
						pending={Boolean(x.uploadPromise)}
						size={x.fileSize}
						isSecureLink={!!sendAttachmentsAsLink}
					/>
				))}
				{savedAttachments?.map(x => (
					<FileChip
						key={x.id}
						name={x.fileName}
						onRemoveClicked={!readonly ? onRemoveSavedEmailAttachment(x) : undefined}
						size={x.fileSize}
						isSecureLink={!!sendAttachmentsAsLink}
					/>
				))}
				{emailAttachments?.attachmentsWithMetadata.map((x, i) => (
					<FileChip
						exceedsMaxByteCount={sendAttachmentsAsLink ? false : x.exceedsMaxByteCount}
						key={`${x.file.name || ''}-${i}`}
						name={x.file.name}
						onRemoveClicked={!readonly ? onRemoveClicked(x.file) : undefined}
						size={x.file.size}
						isSecureLink={!!sendAttachmentsAsLink}
					/>
				))}
			</div>
			{!!sendAttachmentsAsLink && (
				<p className={css(styleSheet.infoText)}>
					Files will be replaced with a secure download link in the email body.
				</p>
			)}
		</div>
	);
});
